import { useCallback, useEffect, useRef, useState } from "react";
import { getAuthToken } from "@/lib/auth";
import { buildAuthenticatedWebSocketConfig } from "@/lib/websocket";
import { normalizeConfiguredUrl } from "@/lib/apiUrl";
import { getWsUrl } from "@/config/runtime";
import {
  subscribeRealtimeSocket,
  type RealtimeConnectionState,
} from "@/lib/sharedRealtimeSocket";

interface RealtimeEvent {
  event?: string;
  [key: string]: unknown;
}

export type ConnectionState = RealtimeConnectionState;

interface UseRealtimeEventsOptions {
  enabled?: boolean;
  /** WebSocket origin override. Defaults to the configured API origin. */
  websocketUrl?: string;
  path?: string;
  onEvent?: (payload: RealtimeEvent) => void;
  /**
   * Called when the channel is connected again after it had dropped (network
   * loss, server restart, or a project channel suspended while the tab was
   * hidden). Events sent while disconnected are lost, so callers use this to
   * catch up. Not called for the initial connection.
   */
  onReconnect?: () => void;
}

export const useRealtimeEvents = ({
  enabled = true,
  websocketUrl,
  path = "/ws/notifications/",
  onEvent,
  onReconnect,
}: UseRealtimeEventsOptions = {}): {
  connectionState: ConnectionState;
  reconnect: () => void;
} => {
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("disconnected");
  const subscriptionRef = useRef<ReturnType<
    typeof subscribeRealtimeSocket
  > | null>(null);
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const token = getAuthToken();

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  useEffect(() => {
    if (!enabled || !token) {
      setConnectionState("disconnected");
      return;
    }
    const base =
      normalizeConfiguredUrl(websocketUrl) || getWsUrl();
    let hasConnected = false;
    let lostConnection = false;
    const subscription = subscribeRealtimeSocket(
      buildAuthenticatedWebSocketConfig(path, token, base),
      {
        onState: (state) => {
          setConnectionState(state);
          if (state !== "connected") {
            if (hasConnected) lostConnection = true;
            return;
          }
          const reconnected = lostConnection;
          hasConnected = true;
          lostConnection = false;
          if (reconnected && getAuthToken() === token) {
            onReconnectRef.current?.();
          }
        },
        onMessage: (event) => {
          // Ignore any queued event from an identity replaced before rerender.
          if (getAuthToken() !== token) return;
          let payload: RealtimeEvent;
          try {
            payload = JSON.parse(event.data) as RealtimeEvent;
          } catch {
            return;
          }
          onEventRef.current?.(payload);
        },
      }
    );
    subscriptionRef.current = subscription;
    return () => {
      subscriptionRef.current = null;
      subscription.release();
    };
  }, [enabled, path, token, websocketUrl]);

  const reconnect = useCallback(() => subscriptionRef.current?.reconnect(), []);
  return { connectionState, reconnect };
};
